import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType, auth, signInWithEmailAndPassword } from '../firebase';
import { Payroll, Employee, UserProfile } from '../types';
import { useAuth } from '../App';
import { 
  DollarSign, 
  Download, 
  Calendar,
  CreditCard,
  FileText,
  AlertCircle,
  Printer,
  Loader2,
  Lock,
  Unlock,
  ShieldAlert,
  Eye,
  Key,
  X,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { cn } from '../lib/utils';
import { downloadFile } from '../lib/downloadHelper';
import toast from 'react-hot-toast';

const MyPayroll: React.FC = () => {
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Filter & Security states
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedMonthFilter, setSelectedMonthFilter] = useState<string>('');
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [targetPayroll, setTargetPayroll] = useState<Payroll | null>(null);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [oldPinInput, setOldPinInput] = useState('');
  const [newPinInput, setNewPinInput] = useState('');
  const [confirmPinInput, setConfirmPinInput] = useState('');
  const [forgotPinMode, setForgotPinMode] = useState(false);
  const [accountPasswordForReset, setAccountPasswordForReset] = useState('');
  const [isYearMonthModalOpen, setIsYearMonthModalOpen] = useState(false);
  const [selectedMonthInput, setSelectedMonthInput] = useState('');
  const [isVerifiedAndShown, setIsVerifiedAndShown] = useState(false);
  const [previewPayroll, setPreviewPayroll] = useState<Payroll | null>(null);
  const [isSettingNewPassword, setIsSettingNewPassword] = useState(false);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);

  // Security Status Calculations
  const hasExistingPin = Boolean(userProfile?.payrollPin);
  const failedPinAttempts = userProfile?.failedPinAttempts || 0;
  const isPayrollLocked = Boolean(userProfile?.isPayrollLocked || failedPinAttempts >= 5);
  const isPinResetByAdmin = Boolean(userProfile?.isPinResetByAdmin);
  const storedPayrollPin = userProfile?.payrollPin || '1234';

  useEffect(() => {
    if (!user?.uid) return;

    // Realtime User Profile Listener for Payroll PIN & Lock status
    const unsubscribeUser = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
      if (docSnap.exists()) {
        setUserProfile({ uid: docSnap.id, ...docSnap.data() } as UserProfile);
      }
    });

    return () => unsubscribeUser();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.email) return;

    const userEmail = user.email.toLowerCase();
    
    let unsubscribePayroll: (() => void) | null = null;

    const unsubscribeEmployee = onSnapshot(
      query(collection(db, 'employees'), where('email', '==', userEmail)),
      (snapshot) => {
        if (!snapshot.empty) {
          const empData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Employee;
          setEmployee(empData);

          if (unsubscribePayroll) unsubscribePayroll();

          // Fetch payrolls using employeeEmail which is optimized for security rules
          unsubscribePayroll = onSnapshot(
            query(collection(db, 'payrolls'), where('employeeEmail', '==', userEmail), orderBy('month', 'desc')),
            (payrollSnapshot) => {
              setPayrolls(payrollSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Payroll)));
              setLoading(false);
            },
            (error) => {
              handleFirestoreError(error, OperationType.LIST, 'payrolls');
              setLoading(false);
            }
          );
        } else {
          setEmployee(null);
          setPayrolls([]);
          setLoading(false);
        }
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, 'employees');
        setLoading(false);
      }
    );

    return () => {
      unsubscribeEmployee();
      if (unsubscribePayroll) unsubscribePayroll();
    };
  }, [user?.email]);

  const verifyAndGenerateSlip = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetPayroll || !user) return;

    // 1. Locked State: Strictly deny access
    if (isPayrollLocked) {
      toast.error('Payroll Feature Blocked! You reached 5 wrong PIN attempts. Admin must reset your PIN via EPAM.');
      return;
    }

    // 2. Admin Reset State: User must verify User Account Password to create a new PIN
    if (isPinResetByAdmin) {
      if (!accountPasswordForReset.trim()) {
        toast.error('Please enter your User Account Password to create new PIN');
        return;
      }
      if (newPinInput.length < 4) {
        toast.error('New PIN must be at least 4 digits/characters long');
        return;
      }
      if (newPinInput !== confirmPinInput) {
        toast.error('New PIN and confirmation PIN do not match');
        return;
      }

      setIsVerifyingPassword(true);
      try {
        await signInWithEmailAndPassword(auth, user.email || '', accountPasswordForReset);
      } catch (err: any) {
        toast.error('Incorrect User Account Password. Password verification failed.');
        setIsVerifyingPassword(false);
        return;
      }

      try {
        await updateDoc(doc(db, 'users', user.uid), {
          payrollPin: newPinInput,
          failedPinAttempts: 0,
          isPayrollLocked: false,
          isPinResetByAdmin: false
        });
        toast.success('New Payroll PIN set successfully!');
        setIsPasswordModalOpen(false);
        setAccountPasswordForReset('');
        setNewPinInput('');
        setConfirmPinInput('');
        setIsVerifiedAndShown(true);
      } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
      } finally {
        setIsVerifyingPassword(false);
      }
      return;
    }

    // 3. Standard Payroll PIN Verification
    if (!enteredPassword.trim()) {
      toast.error('Please enter your Payroll PIN');
      return;
    }

    if (enteredPassword.trim() === storedPayrollPin) {
      // Correct PIN
      try {
        if (failedPinAttempts > 0) {
          await updateDoc(doc(db, 'users', user.uid), { failedPinAttempts: 0 });
        }
      } catch (err) {
        console.warn('Failed to reset attempts:', err);
      }
      toast.success('Identity verified successfully');
      setIsPasswordModalOpen(false);
      setEnteredPassword('');
      setIsVerifiedAndShown(true);
    } else {
      // Wrong PIN entered!
      const newCount = failedPinAttempts + 1;
      const remaining = Math.max(0, 5 - newCount);

      try {
        if (newCount >= 5) {
          await updateDoc(doc(db, 'users', user.uid), {
            failedPinAttempts: 5,
            isPayrollLocked: true
          });
          toast.error('FEATURE LOCKED! You have entered an incorrect PIN 5 times. Payroll access is now blocked until Admin resets it via HR letter.');
        } else {
          await updateDoc(doc(db, 'users', user.uid), {
            failedPinAttempts: newCount
          });
          toast.error(`Incorrect PIN! Attempt ${newCount} of 5. You have ${remaining} attempt(s) remaining before account lockout.`);
        }
      } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
      }
    }
  };

  const generateSalarySlip = (payroll: Payroll, mode: 'download' | 'print' = 'download') => {
    if (!employee) return;

    const doc = new jsPDF();
    
    // Professional Header
    doc.setFillColor(30, 64, 175); // Professional Navy Blue
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('BRIGHT TECHNICAL SYSTEMS', 105, 18, { align: 'center' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Specialists in Air Conditioning, Plumbing & Electrical Works', 105, 24, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('PAYSLIP - ' + payroll.month, 105, 32, { align: 'center' });

    const itemsStatutory = [
      { name: 'NSSF (Employee 10%)', amount: payroll.nssfEmployee },
      { name: 'PAYE (Income Tax)', amount: payroll.paye },
      { name: 'NHIF (Employee 3%)', amount: payroll.nhifEmployee },
      { name: 'HESLB (Loan)', amount: payroll.heslb }
    ].filter(i => i.amount > 0);

    let currentY = 55;
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('EMPLOYEE INFORMATION', 20, currentY);
    doc.setDrawColor(226, 232, 240);
    doc.line(20, currentY + 2, 190, currentY + 2);
    
    currentY += 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setDrawColor(241, 245, 249);
    doc.setFillColor(248, 250, 252);
    doc.rect(20, currentY - 5, 170, 32, 'F');
    
    doc.text(`Employee Name:`, 25, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(`${employee.firstName} ${employee.lastName}`, 60, currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Employee ID:`, 120, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(employee.employeeCode || employee.id.substring(0, 8).toUpperCase(), 150, currentY);
    
    currentY += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Department:`, 25, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(employee.department || 'N/A', 60, currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Position:`, 120, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(employee.position || 'N/A', 150, currentY);
    
    currentY += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`TIN Number:`, 25, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(employee.tin || 'N/A', 60, currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Month / Year:`, 120, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(payroll.month, 150, currentY);

    currentY += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Bank Info:`, 25, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(`${employee.bankName || 'N/A'} - ${employee.accountNumber || 'N/A'}`, 60, currentY);

    currentY += 20;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('EARNINGS', 20, currentY);
    doc.text('DEDUCTIONS', 110, currentY);
    doc.line(20, currentY + 2, 95, currentY + 2);
    doc.line(110, currentY + 2, 190, currentY + 2);
    
    currentY += 10;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let earnY = currentY;
    doc.text('Basic Salary', 25, earnY);
    doc.text(`TZS ${payroll.basicSalary.toLocaleString()}`, 90, earnY, { align: 'right' });
    earnY += 6;

    employee.allowances?.forEach(al => {
      doc.text(al.name, 25, earnY);
      doc.text(`TZS ${al.amount.toLocaleString()}`, 90, earnY, { align: 'right' });
      earnY += 6;
    });
    
    let dedY = currentY;
    itemsStatutory.forEach(s => {
      doc.text(s.name, 115, dedY);
      doc.text(`TZS ${s.amount.toLocaleString()}`, 185, dedY, { align: 'right' });
      dedY += 6;
    });
    
    payroll.deductions.forEach(d => {
      doc.text(d.categoryName, 115, dedY);
      doc.text(`TZS ${d.amount.toLocaleString()}`, 185, dedY, { align: 'right' });
      dedY += 6;
      if (d.totalPrincipal !== undefined && d.totalPrincipal > 0) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        doc.text(`  (Principal: TZS ${d.totalPrincipal.toLocaleString()} | Bal: TZS ${(d.remainingBalance || 0).toLocaleString()} | Left: ${d.monthsRemaining ?? (d.durationMonths || 12) - (d.monthsPaid || 0)} mos)`, 115, dedY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        dedY += 5;
      }
    });
    
    currentY = Math.max(earnY + 10, dedY + 10);
    doc.setDrawColor(226, 232, 240);
    doc.line(20, currentY, 190, currentY);
    currentY += 10;
    
    doc.setFont('helvetica', 'bold');
    doc.text('GROSS EARNINGS:', 25, currentY);
    doc.text(`TZS ${payroll.grossPay.toLocaleString()}`, 90, currentY, { align: 'right' });
    
    doc.text('TOTAL DEDUCTIONS:', 115, currentY);
    doc.text(`TZS ${payroll.totalDeductions.toLocaleString()}`, 185, currentY, { align: 'right' });
    
    currentY += 15;
    doc.setFillColor(30, 64, 185);
    doc.rect(20, currentY - 8, 170, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text('NET PAY (PAYABLE):', 30, currentY + 1);
    doc.text(`TZS ${payroll.netSalary.toLocaleString()}`, 180, currentY + 1, { align: 'right' });
    
    currentY += 40;
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.text('This is a computer generated salary slip and does not require a signature.', 105, currentY, { align: 'center' });
    doc.text('Professional Tanzania Payroll - Compliant with TRA and NSSF Regulations', 105, currentY + 4, { align: 'center' });
    doc.text(`Generated on ${new Date().toLocaleString()}`, 105, currentY + 8, { align: 'center' });

    if (mode === 'print') {
      doc.autoPrint();
      window.open(doc.output('bloburl'), '_blank');
    } else {
      downloadFile(doc.output('blob'), `Salary_Slip_${employee.firstName}_${payroll.month}.pdf`);
    }
  };

  const filteredPayrolls = payrolls.filter(p => {
    if (!p.month) return false;
    if (selectedYear && !p.month.startsWith(selectedYear)) return false;
    if (selectedMonthFilter && p.month !== selectedMonthFilter) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">My Payroll & Salary Slips</h1>
          <p className="text-slate-500 font-medium italic">Securely access your certified Tanzania salary slips</p>
        </div>
        {(!hasExistingPin || isPinResetByAdmin) && (
          <button
            onClick={() => setIsSettingNewPassword(true)}
            className="flex items-center gap-2 px-5 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-800/20 text-sm cursor-pointer"
          >
            <Key className="w-4 h-4" /> {isPinResetByAdmin ? 'Reset & Create New PIN' : 'Create New PIN'}
          </button>
        )}
      </div>

      {isPinResetByAdmin && (
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-2xl text-indigo-900 text-sm font-medium flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-indigo-700 shrink-0" />
            <div>
              <p className="font-bold text-indigo-950">Your Payroll PIN was reset by Admin</p>
              <p className="text-xs text-indigo-700">Please click "Create New PIN" to set up your new 4-digit Payroll PIN.</p>
            </div>
          </div>
          <button
            onClick={() => setIsSettingNewPassword(true)}
            className="px-4 py-2 bg-indigo-800 text-white font-bold text-xs rounded-xl hover:bg-indigo-900 transition-all shrink-0 cursor-pointer shadow-sm"
          >
            Create New PIN
          </button>
        </div>
      )}

      {!employee ? (
        <div className="bg-white p-12 rounded-[2.5rem] border border-slate-100 text-center shadow-sm">
          <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">No employee record linked</h3>
          <p className="text-slate-500 max-w-sm mx-auto">Your email is not linked to any employee record. Please contact HR.</p>
        </div>
      ) : !isVerifiedAndShown ? (
        <div className="bg-white p-12 rounded-[3rem] border border-slate-100 shadow-xl text-center flex flex-col items-center justify-center">
          <div className="w-20 h-20 bg-blue-50 text-blue-800 rounded-3xl flex items-center justify-center mb-6 shadow-inner">
            <FileText className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-2">Access Your Salary Slips</h2>
          <p className="text-slate-500 max-w-md mx-auto mb-8 text-sm">
            Click the button below to select your desired year and month. Secure password verification is required before viewing.
          </p>
          <button
            onClick={() => setIsYearMonthModalOpen(true)}
            className="px-8 py-4 bg-blue-800 hover:bg-blue-900 text-white font-bold rounded-2xl shadow-xl shadow-blue-800/20 transition-all flex items-center gap-3 text-base"
          >
            <Eye className="w-5 h-5" /> View Salary Slip
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 p-4 rounded-2xl">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <h4 className="font-bold text-emerald-900 text-sm">Identity Verified Successfully</h4>
                <p className="text-xs text-emerald-700">Showing certified salary slip for period: <span className="font-bold">{selectedMonthInput}</span></p>
              </div>
            </div>
            <button
              onClick={() => {
                setIsVerifiedAndShown(false);
                setSelectedMonthInput('');
                setTargetPayroll(null);
              }}
              className="px-4 py-2 bg-white border border-emerald-300 text-emerald-800 rounded-xl font-bold text-xs hover:bg-emerald-100 transition-all shadow-sm"
            >
              Select Another Month
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {payrolls
              .filter(p => !selectedMonthInput || p.month === selectedMonthInput)
              .map(p => (
                <div key={p.id} className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
                      <Calendar className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{format(new Date(p.month + '-01'), 'MMMM yyyy')}</h3>
                      <div className="flex items-center gap-3 text-sm mt-1">
                        <span className="text-slate-500 font-medium">Net Salary: <span className="font-black text-emerald-600">TZS {p.netSalary.toLocaleString()}</span></span>
                        <span className="px-3 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-600">
                          {p.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setPreviewPayroll(p)}
                      className="flex items-center gap-1.5 bg-slate-100 text-slate-800 px-4 py-2.5 rounded-xl font-bold text-xs hover:bg-slate-200 transition-all"
                    >
                      <Eye className="w-4 h-4 text-slate-600" />
                      Preview
                    </button>
                    <button
                      onClick={() => generateSalarySlip(p, 'print')}
                      className="flex items-center gap-1.5 bg-teal-50 text-teal-800 px-4 py-2.5 rounded-xl font-bold text-xs hover:bg-teal-100 transition-all"
                    >
                      <Printer className="w-4 h-4 text-teal-600" />
                      Print
                    </button>
                    <button
                      onClick={() => generateSalarySlip(p, 'download')}
                      className="flex items-center gap-1.5 bg-blue-800 text-white px-5 py-2.5 rounded-xl font-bold text-xs hover:bg-blue-900 transition-all shadow-md shadow-blue-800/20"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Year & Month Selection Modal */}
      {isYearMonthModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 w-full max-w-md relative">
            <button onClick={() => setIsYearMonthModalOpen(false)} className="absolute top-6 right-6 p-2 hover:bg-slate-100 rounded-xl">
              <X className="w-5 h-5 text-slate-400" />
            </button>

            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800 mb-4">
              <Calendar className="w-6 h-6" />
            </div>

            <h3 className="text-xl font-black text-slate-900 mb-1">Select Year & Month</h3>
            <p className="text-xs text-slate-500 mb-6">Choose the period for the salary slip you wish to view.</p>

            <form onSubmit={(e) => {
              e.preventDefault();
              if (!selectedMonthInput) {
                toast.error('Please select a valid month');
                return;
              }
              const found = payrolls.find(p => p.month === selectedMonthInput);
              if (!found) {
                toast.error('No salary slip found for ' + selectedMonthInput);
                return;
              }
              setTargetPayroll(found);
              setIsYearMonthModalOpen(false);
              setIsPasswordModalOpen(true);
            }} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Select Month *</label>
                <select
                  required
                  value={selectedMonthInput}
                  onChange={(e) => setSelectedMonthInput(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:ring-2 focus:ring-blue-800/10 focus:outline-none text-sm"
                >
                  <option value="">-- Choose Month --</option>
                  {payrolls.map(p => (
                    <option key={p.id} value={p.month}>
                      {format(new Date(p.month + '-01'), 'MMMM yyyy')} ({p.status.toUpperCase()})
                    </option>
                  ))}
                </select>
                {payrolls.length === 0 && (
                  <p className="text-[11px] text-amber-600 mt-1 font-medium">No payrolls have been processed/sent to your account yet.</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsYearMonthModalOpen(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={payrolls.length === 0}
                  className="flex-1 py-3 bg-blue-800 text-white rounded-xl font-bold hover:bg-blue-900 shadow-lg shadow-blue-200 disabled:opacity-50"
                >
                  View
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Security Password / PIN Verification Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 w-full max-w-md relative">
            <button 
              onClick={() => {
                setIsPasswordModalOpen(false);
                setEnteredPassword('');
                setAccountPasswordForReset('');
                setNewPinInput('');
                setConfirmPinInput('');
              }} 
              className="absolute top-6 right-6 p-2 hover:bg-slate-100 rounded-xl cursor-pointer"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>

            {/* Case A: Locked Access */}
            {isPayrollLocked ? (
              <div className="space-y-5 text-center">
                <div className="w-16 h-16 bg-red-100 text-red-600 rounded-3xl flex items-center justify-center mx-auto shadow-sm">
                  <ShieldAlert className="w-9 h-9" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-red-600 tracking-tight">Payroll Feature Blocked</h3>
                  <p className="text-xs font-bold text-red-500 mt-1">5 Incorrect PIN Attempts Reached</p>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-left space-y-2 text-xs text-red-900 font-medium">
                  <p className="font-bold">Access to open salary slips is denied.</p>
                  <p>
                    To unlock your payroll PIN feature, you must submit an application letter signed by HR to the Administrator.
                  </p>
                  <p className="text-[11px] text-red-700 font-bold italic pt-1">
                    An Admin will verify and reset your access via the EPAM module.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPasswordModalOpen(false)}
                  className="w-full py-3.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all text-xs"
                >
                  Close & Contact Admin
                </button>
              </div>
            ) : isPinResetByAdmin ? (
              /* Case B: Admin Reset completed -> User must enter Account Password to set new PIN */
              <div className="space-y-4">
                <div className="w-12 h-12 bg-blue-50 text-blue-800 rounded-2xl flex items-center justify-center mb-2">
                  <Key className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900">PIN Reset by Admin</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Your payroll PIN was reset via EPAM. Please verify your User Account Password to create a new 4-digit PIN.</p>
                </div>

                <form onSubmit={verifyAndGenerateSlip} className="space-y-3 pt-2">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-600">User Account Password *</label>
                    <input
                      type="password"
                      required
                      value={accountPasswordForReset}
                      onChange={(e) => setAccountPasswordForReset(e.target.value)}
                      placeholder="Enter your account login password..."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-blue-800/10 focus:outline-none text-xs"
                      autoFocus
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-600">New Payroll PIN *</label>
                    <input
                      type="password"
                      required
                      value={newPinInput}
                      onChange={(e) => setNewPinInput(e.target.value)}
                      placeholder="Enter new 4+ digit PIN..."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-blue-800/10 focus:outline-none text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-600">Confirm New Payroll PIN *</label>
                    <input
                      type="password"
                      required
                      value={confirmPinInput}
                      onChange={(e) => setConfirmPinInput(e.target.value)}
                      placeholder="Confirm new PIN..."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-blue-800/10 focus:outline-none text-xs"
                    />
                  </div>

                  <div className="flex gap-3 pt-3">
                    <button
                      type="button"
                      onClick={() => setIsPasswordModalOpen(false)}
                      disabled={isVerifyingPassword}
                      className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isVerifyingPassword}
                      className="flex-1 py-3 bg-blue-800 text-white rounded-xl font-bold hover:bg-blue-900 shadow-lg shadow-blue-200 text-xs flex items-center justify-center gap-2"
                    >
                      {isVerifyingPassword ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Save PIN & View'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              /* Case C: Normal Active PIN verification */
              <div className="space-y-4">
                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800 mb-2">
                  <Lock className="w-6 h-6" />
                </div>

                <div>
                  <h3 className="text-xl font-black text-slate-900 mb-1">Security Verification</h3>
                  <p className="text-xs text-slate-500">Please enter your Payroll Access PIN to view this salary slip.</p>
                </div>

                {failedPinAttempts > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs font-bold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>Warning: {failedPinAttempts} / 5 incorrect attempts. {5 - failedPinAttempts} attempt(s) remaining before account lockout!</span>
                  </div>
                )}

                <form onSubmit={verifyAndGenerateSlip} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-600">Payroll Access PIN *</label>
                    <input
                      type="password"
                      required
                      value={enteredPassword}
                      onChange={(e) => setEnteredPassword(e.target.value)}
                      placeholder="Enter 4-digit PIN..."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-blue-800/10 focus:outline-none"
                      autoFocus
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsPasswordModalOpen(false);
                        setEnteredPassword('');
                      }}
                      className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 py-3 bg-blue-800 text-white rounded-xl font-bold hover:bg-blue-900 shadow-lg shadow-blue-200 text-xs"
                    >
                      Confirm & View
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Setup Dedicated Payroll Password Modal */}
      {isSettingNewPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 w-full max-w-md relative">
            <button onClick={() => {
              setIsSettingNewPassword(false);
              setForgotPinMode(false);
              setOldPinInput('');
              setNewPinInput('');
              setConfirmPinInput('');
              setAccountPasswordForReset('');
            }} className="absolute top-6 right-6 p-2 hover:bg-slate-100 rounded-xl">
              <X className="w-5 h-5 text-slate-400" />
            </button>

            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-800 mb-4">
              <Key className="w-6 h-6" />
            </div>

            <h3 className="text-xl font-black text-slate-900 mb-1">
              {forgotPinMode ? 'Reset PIN via Account Password' : (hasExistingPin ? 'Change Payroll PIN' : 'Setup Dedicated Payroll PIN')}
            </h3>
            <p className="text-xs text-slate-500 mb-6">
              {forgotPinMode 
                ? 'Enter your user account password to reset your payroll PIN.' 
                : (hasExistingPin 
                    ? 'Please enter your current old PIN and new PIN below.' 
                    : 'Create a secure 4+ digit PIN exclusively for your salary slips.')}
            </p>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!user) return;

              if (isPayrollLocked) {
                toast.error('Account locked! You must submit an HR letter to Admin for an EPAM reset.');
                return;
              }

              if (forgotPinMode) {
                if (!accountPasswordForReset.trim()) {
                  toast.error('Please enter your account password');
                  return;
                }
                if (newPinInput.length < 4) {
                  toast.error('New PIN must be at least 4 digits');
                  return;
                }
                if (newPinInput !== confirmPinInput) {
                  toast.error('New PIN and confirmation PIN do not match');
                  return;
                }

                setIsVerifyingPassword(true);
                try {
                  await signInWithEmailAndPassword(auth, user.email || '', accountPasswordForReset);
                  await updateDoc(doc(db, 'users', user.uid), {
                    payrollPin: newPinInput,
                    failedPinAttempts: 0,
                    isPayrollLocked: false,
                    isPinResetByAdmin: false
                  });
                  toast.success('Payroll PIN updated successfully!');
                  setIsSettingNewPassword(false);
                  setForgotPinMode(false);
                  setAccountPasswordForReset('');
                  setNewPinInput('');
                  setConfirmPinInput('');
                } catch (err) {
                  toast.error('Incorrect Account Password verification failed.');
                } finally {
                  setIsVerifyingPassword(false);
                }
              } else {
                if (hasExistingPin) {
                  if (!oldPinInput.trim()) {
                    toast.error('Please enter your current (old) PIN');
                    return;
                  }
                  if (oldPinInput.trim() !== userProfile?.payrollPin) {
                    toast.error('Incorrect current (old) PIN');
                    return;
                  }
                }
                if (newPinInput.length < 4) {
                  toast.error('New PIN must be at least 4 digits');
                  return;
                }
                if (newPinInput !== confirmPinInput) {
                  toast.error('New PIN and confirmation PIN do not match');
                  return;
                }

                setIsVerifyingPassword(true);
                try {
                  await updateDoc(doc(db, 'users', user.uid), {
                    payrollPin: newPinInput,
                    failedPinAttempts: 0,
                    isPayrollLocked: false,
                    isPinResetByAdmin: false
                  });
                  toast.success('Dedicated payroll PIN saved successfully!');
                  setIsSettingNewPassword(false);
                  setOldPinInput('');
                  setNewPinInput('');
                  setConfirmPinInput('');
                } catch (error) {
                  handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
                } finally {
                  setIsVerifyingPassword(false);
                }
              }
            }} className="space-y-4">
              {!forgotPinMode && hasExistingPin && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600">Current (Old) PIN *</label>
                  <input
                    type="password"
                    required
                    value={oldPinInput}
                    onChange={(e) => setOldPinInput(e.target.value)}
                    placeholder="Enter current PIN..."
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-indigo-800/10 focus:outline-none"
                    autoFocus
                  />
                </div>
              )}

              {forgotPinMode && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600">User Account Password *</label>
                  <input
                    type="password"
                    required
                    value={accountPasswordForReset}
                    onChange={(e) => setAccountPasswordForReset(e.target.value)}
                    placeholder="Enter your account password..."
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-indigo-800/10 focus:outline-none"
                    autoFocus
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">New Payroll PIN *</label>
                <input
                  type="password"
                  required
                  value={newPinInput}
                  onChange={(e) => setNewPinInput(e.target.value)}
                  placeholder="Enter new 4+ digit PIN..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-indigo-800/10 focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600">Confirm New PIN *</label>
                <input
                  type="password"
                  required
                  value={confirmPinInput}
                  onChange={(e) => setConfirmPinInput(e.target.value)}
                  placeholder="Confirm new PIN..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold focus:ring-2 focus:ring-indigo-800/10 focus:outline-none"
                />
              </div>

              <div className="flex items-center justify-between text-xs pt-1">
                {hasExistingPin && !forgotPinMode ? (
                  <button
                    type="button"
                    onClick={() => setForgotPinMode(true)}
                    className="text-indigo-600 font-bold hover:underline"
                  >
                    Forgot Old PIN? Reset via Account Password
                  </button>
                ) : forgotPinMode ? (
                  <button
                    type="button"
                    onClick={() => setForgotPinMode(false)}
                    className="text-indigo-600 font-bold hover:underline"
                  >
                    Back to Old PIN verification
                  </button>
                ) : <span />}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsSettingNewPassword(false);
                    setForgotPinMode(false);
                    setOldPinInput('');
                    setNewPinInput('');
                    setConfirmPinInput('');
                    setAccountPasswordForReset('');
                  }}
                  className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isVerifyingPassword}
                  className="flex-1 py-3 bg-indigo-800 text-white rounded-xl font-bold hover:bg-indigo-900 shadow-lg shadow-indigo-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isVerifyingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save PIN'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Salary Slip Preview Modal */}
      {previewPayroll && employee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 w-full max-w-2xl relative my-8 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setPreviewPayroll(null)} className="absolute top-6 right-6 p-2 hover:bg-slate-100 rounded-xl">
              <X className="w-5 h-5 text-slate-400" />
            </button>

            <div className="text-center pb-6 border-b border-slate-100 mb-6">
              <h3 className="text-xl font-black text-slate-900 tracking-tight">BRIGHT TECHNICAL SYSTEMS</h3>
              <p className="text-xs text-slate-500">Certified Salary Slip - {previewPayroll.month}</p>
            </div>

            <div className="space-y-6 text-sm">
              <div className="bg-slate-50 p-4 rounded-2xl grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Employee Name</div>
                  <div className="font-bold text-slate-900">{previewPayroll.employeeName}</div>
                </div>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Period</div>
                  <div className="font-bold text-slate-900">{format(new Date(previewPayroll.month + '-01'), 'MMMM yyyy')}</div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Earnings</h4>
                <div className="flex justify-between py-2 border-b border-slate-100 font-medium">
                  <span className="text-slate-600">Basic Salary</span>
                  <span className="font-bold text-slate-900">TZS {previewPayroll.basicSalary.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100 font-medium">
                  <span className="text-slate-600">Allowances</span>
                  <span className="font-bold text-slate-900">TZS {(previewPayroll.grossPay - previewPayroll.basicSalary).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200 font-black text-slate-900">
                  <span>Gross Pay</span>
                  <span>TZS {previewPayroll.grossPay.toLocaleString()}</span>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Deductions</h4>
                <div className="flex justify-between py-2 border-b border-slate-100 font-medium">
                  <span className="text-slate-600">PAYE (Income Tax)</span>
                  <span className="font-bold text-red-600">-TZS {previewPayroll.paye.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100 font-medium">
                  <span className="text-slate-600">NSSF (Employee 10%)</span>
                  <span className="font-bold text-red-600">-TZS {previewPayroll.nssfEmployee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100 font-medium">
                  <span className="text-slate-600">NHIF (Employee 3%)</span>
                  <span className="font-bold text-red-600">-TZS {previewPayroll.nhifEmployee.toLocaleString()}</span>
                </div>
                {previewPayroll.heslb > 0 && (
                  <div className="flex justify-between py-2 border-b border-slate-100 font-medium">
                    <span className="text-slate-600">HESLB Loan</span>
                    <span className="font-bold text-red-600">-TZS {previewPayroll.heslb.toLocaleString()}</span>
                  </div>
                )}
                {previewPayroll.deductions?.map((d, i) => (
                  <div key={i} className="py-2 border-b border-slate-100 font-medium">
                    <div className="flex justify-between">
                      <span className="text-slate-600">{d.categoryName}</span>
                      <span className="font-bold text-red-600">-TZS {d.amount.toLocaleString()}</span>
                    </div>
                    {d.totalPrincipal !== undefined && d.totalPrincipal > 0 && (
                      <div className="text-[10px] text-slate-500 mt-1 bg-slate-50 p-2 rounded border border-slate-100 grid grid-cols-2 gap-1">
                        <span>Principal: TZS {d.totalPrincipal.toLocaleString()}</span>
                        <span>Balance: TZS {(d.remainingBalance || 0).toLocaleString()}</span>
                        <span>Duration: {d.durationMonths || 12} mos</span>
                        <span>Time Remaining: {d.monthsRemaining ?? (d.durationMonths || 12) - (d.monthsPaid || 0)} mos</span>
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex justify-between py-2 border-b border-slate-200 font-black text-red-700">
                  <span>Total Deductions</span>
                  <span>-TZS {previewPayroll.totalDeductions.toLocaleString()}</span>
                </div>
              </div>

              <div className="bg-emerald-50 p-4 rounded-2xl flex items-center justify-between">
                <span className="font-bold text-emerald-900">Net Payable Salary</span>
                <span className="text-lg font-black text-emerald-700">TZS {previewPayroll.netSalary.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex gap-3 pt-6 mt-6 border-t border-slate-100">
              <button
                onClick={() => setPreviewPayroll(null)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200"
              >
                Close
              </button>
              <button
                onClick={() => {
                  generateSalarySlip(previewPayroll, 'print');
                  setPreviewPayroll(null);
                }}
                className="flex-1 py-3 bg-teal-700 text-white rounded-xl font-bold hover:bg-teal-800 shadow-md"
              >
                Print Slip
              </button>
              <button
                onClick={() => {
                  generateSalarySlip(previewPayroll, 'download');
                  setPreviewPayroll(null);
                }}
                className="flex-1 py-3 bg-blue-800 text-white rounded-xl font-bold hover:bg-blue-900 shadow-lg shadow-blue-200"
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPayroll;

